import React, { useState, useEffect, useCallback } from "react";
import {
  Globe, FileText, Shield, AlertTriangle, ChevronRight, ArrowLeft,
  RefreshCw, Plus, Search, Clock, CheckCircle2, ExternalLink,
  TrendingUp, BookOpen, Scale, Activity
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const REGION_COLORS = {
  GLOBAL: "#C9A84C", US: "#3B82F6", EU: "#8B5CF6", BR: "#10B981",
  CA: "#EF4444", UK: "#6366F1", JP: "#F59E0B", LATAM: "#14B8A6", APAC: "#EC4899",
};

function RegionCard({ region, onClick }) {
  const color = REGION_COLORS[region.code] || "#8B95A5";
  return (
    <div
      onClick={() => onClick(region.code)}
      className="rounded-xl p-5 border cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg"
      style={{ borderColor: `${color}30`, background: `${color}08` }}
      data-testid={`region-card-${region.code}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold" style={{ color }}>{region.flag}</span>
          <h3 className="text-base font-semibold text-white">{region.label}</h3>
        </div>
        <ChevronRight size={16} className="text-[#555]" />
      </div>
      <p className="text-xs text-[#8B95A5] mb-3 line-clamp-2">{region.description}</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg py-1.5 bg-[rgba(255,255,255,0.03)]">
          <div className="text-sm font-bold text-white">{region.document_count}</div>
          <div className="text-[9px] text-[#555]">Documents</div>
        </div>
        <div className="rounded-lg py-1.5 bg-[rgba(255,255,255,0.03)]">
          <div className="text-sm font-bold text-white">{region.obligation_count}</div>
          <div className="text-[9px] text-[#555]">Obligations</div>
        </div>
        <div className="rounded-lg py-1.5 bg-[rgba(255,255,255,0.03)]">
          <div className="text-sm font-bold text-white">{region.sota_updates}</div>
          <div className="text-[9px] text-[#555]">SOTA</div>
        </div>
      </div>
      {region.key_standards?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {region.key_standards.slice(0, 3).map(s => (
            <span key={s} className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.05)] text-[#8B95A5]">{s}</span>
          ))}
          {region.key_standards.length > 3 && (
            <span className="text-[9px] text-[#555]">+{region.key_standards.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

function SOTAForm({ onSubmit, onCancel, regions }) {
  const [form, setForm] = useState({ region: "", standard: "", title: "", description: "", effective_date: "", impact_level: "medium", source_url: "" });
  return (
    <div className="rounded-xl border border-[rgba(201,168,76,0.3)] bg-[rgba(201,168,76,0.05)] p-4 mb-6" data-testid="sota-form">
      <h3 className="text-sm font-semibold text-[#C9A84C] mb-3 flex items-center gap-2"><Plus size={14} /> Register SOTA Update</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })}
          className="bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white" data-testid="sota-region">
          <option value="">Region...</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input value={form.standard} onChange={e => setForm({ ...form, standard: e.target.value })} placeholder="Standard (e.g. ISO 13485:2025)"
          className="bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white" data-testid="sota-standard" />
        <select value={form.impact_level} onChange={e => setForm({ ...form, impact_level: e.target.value })}
          className="bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white" data-testid="sota-impact">
          <option value="low">Low Impact</option>
          <option value="medium">Medium Impact</option>
          <option value="high">High Impact</option>
        </select>
      </div>
      <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Update title"
        className="w-full bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white mb-3" data-testid="sota-title" />
      <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description of the regulatory change..."
        className="w-full bg-[#0D0F14] border border-[rgba(255,255,255,0.1)] rounded px-3 py-2 text-sm text-white mb-3 h-20" data-testid="sota-description" />
      <div className="flex gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 bg-[rgba(255,255,255,0.06)] rounded text-sm text-[#8B95A5]">Cancel</button>
        <button onClick={() => onSubmit(form)} className="px-4 py-1.5 bg-[#C9A84C] text-[#0D0F14] rounded text-sm font-semibold" data-testid="sota-submit">Register & Create Change Order</button>
      </div>
    </div>
  );
}

export default function GeoLibraryPage() {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionDocs, setRegionDocs] = useState([]);
  const [regionObls, setRegionObls] = useState([]);
  const [sotaUpdates, setSotaUpdates] = useState([]);
  const [complianceMatrix, setComplianceMatrix] = useState(null);
  const [showSOTAForm, setShowSOTAForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchRegions = useCallback(async () => {
    setLoading(true);
    const opts = { credentials: "include" };
    const [r, m, s] = await Promise.all([
      fetch(`${API}/api/geo-library/regions`, opts).then(r => r.ok ? r.json() : { regions: [] }),
      fetch(`${API}/api/geo-library/compliance-matrix`, opts).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/geo-library/sota/updates?limit=10`, opts).then(r => r.ok ? r.json() : { updates: [] }),
    ]);
    setRegions(r.regions || []);
    setComplianceMatrix(m);
    setSotaUpdates(s.updates || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchRegions(); }, [fetchRegions]);

  const selectRegion = async (code) => {
    setSelectedRegion(code);
    const opts = { credentials: "include" };
    const [docs, obls] = await Promise.all([
      fetch(`${API}/api/geo-library/regions/${code}/documents`, opts).then(r => r.ok ? r.json() : { documents: [] }),
      fetch(`${API}/api/geo-library/regions/${code}/obligations`, opts).then(r => r.ok ? r.json() : { obligations: [] }),
    ]);
    setRegionDocs(docs.documents || []);
    setRegionObls(obls.obligations || []);
  };

  const submitSOTA = async (form) => {
    const res = await fetch(`${API}/api/geo-library/sota/register`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setShowSOTAForm(false);
      fetchRegions();
    }
  };

  const regionMeta = selectedRegion ? regions.find(r => r.code === selectedRegion) : null;
  const regionColor = REGION_COLORS[selectedRegion] || "#8B95A5";

  return (
    <div className="min-h-screen bg-[#0D0F14] text-white p-6 lg:p-10" data-testid="geo-library-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {selectedRegion && (
            <button onClick={() => setSelectedRegion(null)} className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded-lg" data-testid="back-to-regions">
              <ArrowLeft size={18} className="text-[#8B95A5]" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3" data-testid="geo-library-title">
              <Globe size={24} className="text-[#C9A84C]" />
              {selectedRegion ? `${regionMeta?.label || selectedRegion} Library` : "Geography-Based Document Libraries"}
            </h1>
            <p className="text-sm text-[#8B95A5] mt-0.5">
              {selectedRegion ? regionMeta?.description : "Browse regulatory documents, obligations, and SOTA updates by jurisdiction"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSOTAForm(!showSOTAForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] rounded-lg text-sm text-[#C9A84C] hover:bg-[rgba(201,168,76,0.2)]"
            data-testid="new-sota-btn">
            <Plus size={14} /> Register SOTA Update
          </button>
          <button onClick={fetchRegions} className="p-1.5 hover:bg-[rgba(255,255,255,0.06)] rounded-lg">
            <RefreshCw size={16} className={`text-[#8B95A5] ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* SOTA Registration Form */}
      {showSOTAForm && <SOTAForm onSubmit={submitSOTA} onCancel={() => setShowSOTAForm(false)} regions={Object.keys(REGION_COLORS)} />}

      {/* Region Overview (no region selected) */}
      {!selectedRegion && (
        <>
          {/* Region Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {regions.map(r => <RegionCard key={r.code} region={r} onClick={selectRegion} />)}
          </div>

          {/* Recent SOTA Updates */}
          {sotaUpdates.length > 0 && (
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden mb-6">
              <div className="bg-[rgba(255,255,255,0.03)] px-4 py-2 flex items-center gap-2">
                <TrendingUp size={14} className="text-[#C9A84C]" />
                <span className="text-xs text-[#8B95A5] uppercase tracking-wider">Recent AutoSOTA Updates</span>
              </div>
              <div className="divide-y divide-[rgba(255,255,255,0.04)]">
                {sotaUpdates.map(u => {
                  const impactColor = { high: "#F87171", medium: "#FBBF24", low: "#4ADE80" }[u.impact_level] || "#8B95A5";
                  return (
                    <div key={u.sota_id} className="px-4 py-3 flex items-center gap-3 hover:bg-[rgba(255,255,255,0.02)]" data-testid={`sota-${u.sota_id}`}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${REGION_COLORS[u.region] || "#555"}20` }}>
                        <Scale size={14} style={{ color: REGION_COLORS[u.region] || "#555" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{u.title}</div>
                        <div className="text-[10px] text-[#555]">{u.region} | {u.standard || "N/A"} | {new Date(u.detected_at).toLocaleDateString()}</div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ color: impactColor, background: `${impactColor}15` }}>{u.impact_level}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-[rgba(255,255,255,0.05)] text-[#8B95A5]">{u.status}</span>
                      {u.change_order_id && <span className="text-[10px] text-[#36A9E1] font-mono">{u.change_order_id}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Region Detail View */}
      {selectedRegion && regionMeta && (
        <>
          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-xl p-3 border" style={{ borderColor: `${regionColor}30` }}>
              <FileText size={14} style={{ color: regionColor }} />
              <div className="text-xl font-bold text-white mt-1">{regionDocs.length}</div>
              <div className="text-[10px] text-[#8B95A5]">Documents</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: `${regionColor}30` }}>
              <Shield size={14} style={{ color: regionColor }} />
              <div className="text-xl font-bold text-white mt-1">{regionObls.length}</div>
              <div className="text-[10px] text-[#8B95A5]">Obligations</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: `${regionColor}30` }}>
              <BookOpen size={14} style={{ color: regionColor }} />
              <div className="text-xl font-bold text-white mt-1">{regionMeta.key_standards?.length || 0}</div>
              <div className="text-[10px] text-[#8B95A5]">Key Standards</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: `${regionColor}30` }}>
              <Activity size={14} style={{ color: regionColor }} />
              <div className="text-xl font-bold text-white mt-1">{regionMeta.regulatory_bodies?.length || 0}</div>
              <div className="text-[10px] text-[#8B95A5]">Regulatory Bodies</div>
            </div>
          </div>

          {/* Key Standards + Regulatory Bodies */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4">
              <h3 className="text-xs font-semibold text-[#C9A84C] mb-2 uppercase tracking-wider">Key Standards</h3>
              <div className="flex flex-wrap gap-1.5">
                {regionMeta.key_standards?.map(s => (
                  <span key={s} className="text-xs px-2 py-1 rounded-lg bg-[rgba(255,255,255,0.05)] text-white">{s}</span>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] p-4">
              <h3 className="text-xs font-semibold text-[#C9A84C] mb-2 uppercase tracking-wider">Regulatory Bodies</h3>
              <div className="flex flex-wrap gap-1.5">
                {regionMeta.regulatory_bodies?.map(b => (
                  <span key={b} className="text-xs px-2 py-1 rounded-lg bg-[rgba(255,255,255,0.05)] text-white">{b}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden mb-6">
            <div className="bg-[rgba(255,255,255,0.03)] px-4 py-2">
              <span className="text-xs text-[#8B95A5] uppercase tracking-wider">Documents — {regionMeta.label}</span>
            </div>
            {regionDocs.length > 0 ? (
              <table className="w-full" data-testid="region-docs-table">
                <thead><tr className="bg-[rgba(255,255,255,0.02)]">
                  <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">ID</th>
                  <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Title</th>
                  <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Class</th>
                  <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Status</th>
                  <th className="text-left py-2 px-3 text-[10px] text-[#8B95A5] uppercase">Version</th>
                </tr></thead>
                <tbody>
                  {regionDocs.map(d => (
                    <tr key={d.doc_id} className="border-b border-[rgba(255,255,255,0.04)]">
                      <td className="py-2 px-3 text-[11px] font-mono text-[#C9A84C]">{d.doc_id}</td>
                      <td className="py-2 px-3 text-[11px] text-white">{d.name || d.title}</td>
                      <td className="py-2 px-3 text-[11px] text-[#8B95A5]">{d.doc_class}</td>
                      <td className="py-2 px-3"><span className={`text-[10px] px-1.5 py-0.5 rounded ${d.status === "released" ? "bg-[rgba(74,222,128,0.1)] text-[#4ADE80]" : "bg-[rgba(251,191,36,0.1)] text-[#FBBF24]"}`}>{d.status}</span></td>
                      <td className="py-2 px-3 text-[11px] text-[#8B95A5]">v{d.revision || d.version || "1"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-[#555] text-sm" data-testid="no-region-docs">No documents filed for this region yet</div>
            )}
          </div>

          {/* Obligations */}
          {regionObls.length > 0 && (
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] overflow-hidden">
              <div className="bg-[rgba(255,255,255,0.03)] px-4 py-2">
                <span className="text-xs text-[#8B95A5] uppercase tracking-wider">Regulatory Obligations — {regionMeta.label}</span>
              </div>
              <div className="divide-y divide-[rgba(255,255,255,0.04)]">
                {regionObls.map((o, i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="text-sm text-white">{o.title || o.obligation_id}</div>
                    <div className="text-[10px] text-[#8B95A5] mt-0.5">{o.category} | {o.description?.slice(0, 120)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
